// @ts-check
// Editor de texto formatado das notas (doc 10, seção 7, sem BlockNote): área editável com barra de títulos, negrito,
// itálico, tachado, listas, lista de tarefas, citação e link. Grava markdown (lib/rich-text.js) com espera de 500 ms,
// como o Twenty, e cola sempre como texto simples para não trazer estilos de fora. "/" abre o menu de blocos (títulos,
// listas, lista de tarefas, citação, divisor) e "@" menciona um membro da equipe (que recebe aviso) ou um registro.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { htmlToMarkdown, markdownToHtml } from '../../lib/rich-text.js';
import { detectEditorTrigger, filterMentionMembers, filterSlashCommands } from '../../lib/rich-text-commands.js';
import { formatFullName } from '../../lib/field-metadata.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { searchRecords } from '../../data/rest.js';
import { Avatar } from '../../ui/avatar.js';
import { Icon } from '../../ui/icon.js';
import { MenuItem } from '../../ui/dropdown.js';
import { openRecord } from '../side-panel/open-record.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

const SERIALIZE_DELAY_MILLISECONDS = 500;

/** @type {Array<{ id: string, icon: string, command: string, value?: string }>} */
const TOOLBAR_ACTIONS = [
  { id: 'heading', icon: 'IconH2', command: 'formatBlock', value: 'H2' },
  { id: 'subheading', icon: 'IconH3', command: 'formatBlock', value: 'H3' },
  { id: 'bold', icon: 'IconBold', command: 'bold' },
  { id: 'italic', icon: 'IconItalic', command: 'italic' },
  { id: 'strike', icon: 'IconStrikethrough', command: 'strikeThrough' },
  { id: 'bulletList', icon: 'IconList', command: 'insertUnorderedList' },
  { id: 'numberedList', icon: 'IconListNumbers', command: 'insertOrderedList' },
  { id: 'checklist', icon: 'IconListCheck', command: 'checklist' },
  { id: 'quote', icon: 'IconBlockquote', command: 'formatBlock', value: 'BLOCKQUOTE' },
  { id: 'link', icon: 'IconLink', command: 'createLink' },
];

/**
 * Lista de tarefas: a lista atual vira (ou deixa de ser) checklist; clicar no marcador alterna o item.
 * @param {HTMLElement} editor
 */
function toggleChecklist(editor) {
  const selection = window.getSelection();
  const anchor = selection?.anchorNode;
  let list = anchor ? /** @type {HTMLElement | null} */ (anchor.nodeType === 1 ? anchor : anchor.parentElement)?.closest('ul, ol') : null;
  if (!list || !editor.contains(list)) {
    document.execCommand('insertUnorderedList');
    const refreshed = window.getSelection()?.anchorNode;
    list = refreshed ? /** @type {HTMLElement | null} */ (refreshed.nodeType === 1 ? refreshed : refreshed.parentElement)?.closest('ul') : null;
    if (!list) return;
  }
  const listElement = /** @type {HTMLElement} */ (list);
  const isChecklist = listElement.dataset.checklist === 'true';
  if (isChecklist) delete listElement.dataset.checklist;
  else listElement.dataset.checklist = 'true';
  for (const item of listElement.querySelectorAll('li')) {
    if (isChecklist) delete /** @type {HTMLElement} */ (item).dataset.checked;
    else /** @type {HTMLElement} */ (item).dataset.checked = /** @type {HTMLElement} */ (item).dataset.checked ?? 'false';
  }
}

/**
 * Texto do nó do cursor até o cursor, para achar "/" e "@".
 * @returns {{ node: Text, offset: number, text: string } | null}
 */
function readCaretText() {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null;
  const node = selection.anchorNode;
  if (!node || node.nodeType !== 3) return null;
  return { node: /** @type {Text} */ (node), offset: selection.anchorOffset, text: (node.textContent ?? '').slice(0, selection.anchorOffset) };
}

/**
 * Seleciona o gatilho e o que foi digitado depois dele, para o comando escolhido substituir.
 * @param {{ node: Text, offset: number }} caret @param {number} triggerOffset
 */
function selectTriggerText(caret, triggerOffset) {
  const range = document.createRange();
  range.setStart(caret.node, triggerOffset);
  range.setEnd(caret.node, caret.offset);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

/** @param {string} text */
const escapeText = (text) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * @typedef {{ trigger: '/' | '@', query: string, offset: number, node: Text, caretOffset: number, top: number, left: number }} EditorMenuState
 * @typedef {{ key: string, icon?: string, avatar?: { label: string, seed: string, url?: string | null }, text: string, contextualText?: string,
 *   run: () => void }} EditorMenuItem
 */

/**
 * @param {{ value: string, onChange: (markdown: string) => void, placeholder?: string, readOnly?: boolean, autoFocus?: boolean, allowMentions?: boolean }} props
 *   allowMentions: falso onde a menção não avisaria ninguém (texto de painel)
 */
export function RichTextEditor({ value, onChange, placeholder, readOnly = false, autoFocus = false, allowMentions = true }) {
  const editorRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const timerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const lastEmittedRef = useRef(value);
  const [menu, setMenu] = useState(/** @type {EditorMenuState | null} */ (null));
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [recordResults, setRecordResults] = useState(/** @type {any[]} */ ([]));
  const members = workspaceMembersState.value ?? [];

  // o conteúdo só é reescrito quando o valor muda por fora (outra nota), para não perder o cursor
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || value === lastEmittedRef.current && editor.innerHTML) return;
    editor.innerHTML = markdownToHtml(value);
    lastEmittedRef.current = value;
  }, [value]);

  useEffect(() => {
    if (autoFocus) requestAnimationFrame(() => editorRef.current?.focus());
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // menção a registros: busca global a partir de dois caracteres
  const mentionQuery = menu?.trigger === '@' ? menu.query.trim() : '';
  useEffect(() => {
    if (mentionQuery.length < 2) {
      setRecordResults([]);
      return undefined;
    }
    let isCancelled = false;
    const timer = setTimeout(() => {
      searchRecords(mentionQuery, { limit: 5 })
        .then((results) => {
          // membros já vêm da lista da equipe; a busca entra só com registros
          if (!isCancelled) setRecordResults(results.filter((result) => result.objectNameSingular !== 'workspaceMember'));
        })
        .catch(() => {});
    }, 200);
    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [mentionQuery]);

  const emit = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const editor = editorRef.current;
    if (!editor) return;
    const markdown = htmlToMarkdown(editor);
    if (markdown === lastEmittedRef.current) return;
    lastEmittedRef.current = markdown;
    onChange(markdown);
  };

  const scheduleEmit = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(emit, SERIALIZE_DELAY_MILLISECONDS);
  };

  const runAction = (/** @type {(typeof TOOLBAR_ACTIONS)[number]} */ action) => {
    const editor = editorRef.current;
    if (!editor || readOnly) return;
    editor.focus();
    if (action.command === 'checklist') toggleChecklist(editor);
    else if (action.command === 'createLink') {
      const url = window.prompt(t('parity.notes.linkPrompt'), 'https://');
      if (!url || !/^(https?:\/\/|mailto:)/i.test(url)) return;
      document.execCommand('createLink', false, url);
    } else if (action.command === 'formatBlock') {
      const current = document.queryCommandValue('formatBlock').toUpperCase();
      document.execCommand('formatBlock', false, current === action.value ? 'P' : action.value);
    } else document.execCommand(action.command);
    scheduleEmit();
  };

  /** Abre, atualiza ou fecha o menu conforme o texto antes do cursor. */
  const refreshMenu = () => {
    const editor = editorRef.current;
    const caret = readCaretText();
    const found = caret && editor?.contains(caret.node) ? detectEditorTrigger(caret.text) : null;
    const detected = found && (allowMentions || found.trigger !== '@') ? found : null;
    if (!caret || !detected || !editor) {
      if (menu) setMenu(null);
      return;
    }
    if (detected.trigger === '@' && members.length === 0) loadWorkspaceMembers();
    const range = document.createRange();
    range.setStart(caret.node, detected.offset);
    range.setEnd(caret.node, caret.offset);
    const rectangle = range.getBoundingClientRect();
    if (!menu || menu.trigger !== detected.trigger || menu.offset !== detected.offset) setFocusedIndex(0);
    setMenu({ ...detected, node: caret.node, caretOffset: caret.offset, top: rectangle.bottom + 4, left: rectangle.left });
  };

  /** @param {string} command */
  const applySlashCommand = (command) => {
    const editor = editorRef.current;
    if (!editor || !menu) return;
    editor.focus();
    selectTriggerText({ node: menu.node, offset: menu.caretOffset }, menu.offset);
    document.execCommand('delete');
    const blockByCommand = /** @type {Record<string, string>} */ ({ heading1: 'H1', heading2: 'H2', heading3: 'H3', quote: 'BLOCKQUOTE', paragraph: 'P' });
    if (blockByCommand[command]) document.execCommand('formatBlock', false, blockByCommand[command]);
    else if (command === 'bulletList') document.execCommand('insertUnorderedList');
    else if (command === 'numberedList') document.execCommand('insertOrderedList');
    else if (command === 'checklist') toggleChecklist(editor);
    else if (command === 'divider') document.execCommand('insertHorizontalRule');
    setMenu(null);
    scheduleEmit();
  };

  /** @param {string} objectNameSingular @param {string} recordId @param {string} label */
  const insertMention = (objectNameSingular, recordId, label) => {
    const editor = editorRef.current;
    if (!editor || !menu) return;
    editor.focus();
    selectTriggerText({ node: menu.node, offset: menu.caretOffset }, menu.offset);
    const href = objectNameSingular === 'workspaceMember' ? '' : ` href="/object/${objectNameSingular}/${recordId}"`;
    document.execCommand('insertHTML', false,
      `<a class="mention-chip" data-mention="${objectNameSingular}/${recordId}" contenteditable="false"${href}>@${escapeText(label)}</a>&nbsp;`);
    setMenu(null);
    emit();
  };

  /** @type {EditorMenuItem[]} */
  const menuItems = !menu
    ? []
    : menu.trigger === '/'
      ? filterSlashCommands(menu.query, t).map((command) => ({
          key: command.id, icon: command.icon, text: t(command.labelKey), run: () => applySlashCommand(command.id),
        }))
      : [
          ...filterMentionMembers(members, menu.query).map((member) => {
            const name = formatFullName(member.name) || member.userEmail || '';
            return {
              key: `member-${member.id}`, text: name, contextualText: t('recordPage.mentions.member'),
              avatar: { label: name, seed: member.id, url: member.avatarUrl ?? null },
              run: () => insertMention('workspaceMember', member.id, name),
            };
          }),
          ...recordResults.map((result) => ({
            key: `record-${result.objectNameSingular}-${result.recordId}`, text: result.label || t('record.untitled'),
            contextualText: translateMetadataLabel(result.objectLabelSingular ?? ''),
            avatar: { label: result.label || '-', seed: result.recordId, url: result.imageUrl ?? null },
            run: () => insertMention(result.objectNameSingular, result.recordId, result.label || t('record.untitled')),
          })),
        ];

  return html`<div class="rich-text-editor" data-readonly=${readOnly ? 'true' : undefined}>
    ${readOnly
      ? null
      : html`<div class="rich-text-toolbar" role="toolbar" aria-label=${t('parity.notes.toolbar')}>
          ${TOOLBAR_ACTIONS.map((action) => html`<button
            key=${action.id}
            type="button"
            class="rich-text-toolbar-button"
            title=${t(`parity.notes.actions.${action.id}`)}
            aria-label=${t(`parity.notes.actions.${action.id}`)}
            onMouseDown=${(/** @type {MouseEvent} */ event) => event.preventDefault()}
            onClick=${() => runAction(action)}
          ><${Icon} name=${action.icon} size=${16} /></button>`)}
        </div>`}
    <div
      ref=${editorRef}
      class="rich-text-content"
      contenteditable=${readOnly ? 'false' : 'true'}
      role="textbox"
      aria-multiline="true"
      data-placeholder=${placeholder}
      onInput=${() => {
        scheduleEmit();
        refreshMenu();
      }}
      onBlur=${() => {
        emit();
        // o clique no menu chega depois do blur
        setTimeout(() => setMenu(null), 150);
      }}
      onClick=${(/** @type {MouseEvent} */ event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const mention = target.closest?.('a.mention-chip');
        if (mention) {
          event.preventDefault();
          const [objectNameSingular, recordId] = String(/** @type {HTMLElement} */ (mention).dataset.mention ?? '').split('/');
          if (objectNameSingular && recordId && objectNameSingular !== 'workspaceMember') openRecord({ objectNameSingular, recordId, source: target });
          return;
        }
        const item = target.closest?.('ul[data-checklist="true"] > li');
        // o marcador ocupa os primeiros 22 px do item (padding do CSS)
        if (item && !readOnly && event.offsetX < 22) {
          /** @type {HTMLElement} */ (item).dataset.checked = /** @type {HTMLElement} */ (item).dataset.checked === 'true' ? 'false' : 'true';
          emit();
        }
      }}
      onPaste=${(/** @type {ClipboardEvent} */ event) => {
        event.preventDefault();
        document.execCommand('insertText', false, event.clipboardData?.getData('text/plain') ?? '');
      }}
      onKeyDown=${(/** @type {KeyboardEvent} */ event) => {
        if (menu && menuItems.length > 0) {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setFocusedIndex((index) => (index + step + menuItems.length) % menuItems.length);
            return;
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            menuItems[Math.min(focusedIndex, menuItems.length - 1)]?.run();
            return;
          }
        }
        if (event.key === 'Escape') {
          if (menu) {
            event.preventDefault();
            event.stopPropagation();
            setMenu(null);
            return;
          }
          /** @type {HTMLElement} */ (event.currentTarget).blur();
        }
      }}
    ></div>
    ${menu && menuItems.length > 0
      ? html`<div class="rich-text-menu" role="listbox" data-testid=${menu.trigger === '/' ? 'slash-menu' : 'mention-menu'} data-dropdown-surface="true"
          style=${{ top: `${menu.top}px`, left: `${menu.left}px` }} onMouseDown=${(/** @type {MouseEvent} */ event) => event.preventDefault()}>
          ${menuItems.map((item, index) => html`<${MenuItem}
            key=${item.key}
            focused=${index === focusedIndex}
            icon=${item.icon}
            leftComponent=${item.avatar ? html`<${Avatar} placeholder=${item.avatar.label} seed=${item.avatar.seed} size="sm" type="rounded" avatarUrl=${item.avatar.url ?? null} />` : undefined}
            text=${item.text}
            contextualText=${item.contextualText}
            onClick=${item.run}
          />`)}
        </div>`
      : null}
  </div>`;
}

/**
 * Markdown renderizado (somente leitura), usado na prévia e no cartão.
 * @param {{ markdown: string, className?: string }} props
 */
export function RichTextView({ markdown, className = '' }) {
  return html`<div class=${`rich-text-content ${className}`} dangerouslySetInnerHTML=${{ __html: markdownToHtml(markdown) }}></div>`;
}
